import { adviserFactsheetOf } from './client-factsheet';
import type { QueueClient } from '../types/client';

export const CLIENT_TIERS = ['V.VIP', 'VIP', 'Normal'] as const;
export type ClientTier = (typeof CLIENT_TIERS)[number];
export type TierBucket = { tier: ClientTier; count: number };

export const AGE_BUCKETS = ['under30', '30s', '40s', '50s', '60plus'] as const;
export type AgeBucket = (typeof AGE_BUCKETS)[number];
export type AgeBucketCount = { bucket: AgeBucket; count: number };

export const COVERAGE_KEYS = ['life', 'ci', 'medical', 'saving', 'peer'] as const;
export type CoverageKey = (typeof COVERAGE_KEYS)[number];
export type CoverageSegment = { key: CoverageKey; score: number | null; covered: boolean };

/**
 * Matches `getScoreColor` in utils/analysis.js, where 75 is the top band.
 * A segment reads as covered only when it clears that same bar, so the meter
 * and the radar tell the same story.
 */
export const COVERAGE_GOOD_SCORE = 75;

/** Scores as returned by `getClientAnalysisScores` in utils/client.js. */
export type AnalysisScores = {
  lifeScore?: number;
  ciScore?: number;
  medicalScore?: number;
  savingScore?: number;
  peerScore?: number;
};

const importanceOf = (client: QueueClient, adviserEmail?: string): ClientTier => {
  const raw = adviserFactsheetOf(client, adviserEmail)?.importance;
  const match = CLIENT_TIERS.find(tier => tier === raw);
  return match || 'Normal';
};

export const bucketClientsByTier = (clients: QueueClient[], adviserEmail?: string): TierBucket[] => {
  const counts = new Map<ClientTier, number>(CLIENT_TIERS.map(tier => [tier, 0]));
  clients.forEach(client => {
    const tier = importanceOf(client, adviserEmail);
    counts.set(tier, (counts.get(tier) || 0) + 1);
  });
  return CLIENT_TIERS.map(tier => ({ tier, count: counts.get(tier) || 0 }));
};

const bucketForAge = (age: number): AgeBucket => {
  if (age < 30) return 'under30';
  if (age < 40) return '30s';
  if (age < 50) return '40s';
  if (age < 60) return '50s';
  return '60plus';
};

export const bucketClientsByAge = (clients: QueueClient[], adviserEmail?: string): AgeBucketCount[] => {
  const counts = new Map<AgeBucket, number>(AGE_BUCKETS.map(bucket => [bucket, 0]));
  clients.forEach(client => {
    const age = adviserFactsheetOf(client, adviserEmail)?.age;
    if (typeof age === 'number' && Number.isFinite(age) && age >= 0) {
      const bucket = bucketForAge(age);
      counts.set(bucket, (counts.get(bucket) || 0) + 1);
    }
  });
  return AGE_BUCKETS.map(bucket => ({ bucket, count: counts.get(bucket) || 0 }));
};

export const getCoverageSegments = (scores: AnalysisScores): CoverageSegment[] => {
  const byKey: Record<CoverageKey, number | undefined> = {
    life: scores.lifeScore,
    ci: scores.ciScore,
    medical: scores.medicalScore,
    saving: scores.savingScore,
    peer: scores.peerScore
  };

  return COVERAGE_KEYS.map(key => {
    const score = byKey[key];
    const usable = typeof score === 'number' && Number.isFinite(score);
    return {
      key,
      score: usable ? score : null,
      covered: usable && score >= COVERAGE_GOOD_SCORE
    };
  });
};
